import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Plus } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/kit";
import { Switch } from "@/components/ui/switch";
import { HelperCard, IngredientCard, SavedLine, WarningCard } from "@/components/playbook/PlaybookCards";
import { HelpCardEditor } from "@/components/playbook/HelpCardEditor";
import { PlaybookPdfButton } from "@/components/playbook/PlaybookPdfButton";

export const PLAYBOOK_EVENT = "mm-playbook-seen";

const Section = ({ title, sub, children, testId }) => (
  <section className="mt-10" data-testid={testId}>
    <h2 className="text-xl font-bold">{title}</h2>
    {sub && <p className="text-sm text-mm-muted mt-1">{sub}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

const StillGrowing = ({ items }) => (
  <Section title="Still growing" sub="These need a few more uses before your playbook can tell how well they work for you." testId="playbook-still-growing">
    <div className="flex flex-wrap gap-2">{items.map((g) => <span key={g.title} className="mm-chip !cursor-default !text-xs">{g.title} · {g.uses} of {g.needed}</span>)}</div>
  </Section>
);

const Growing = ({ pb }) => (
  <div className="rounded-3xl p-6 bg-[#F3EEFC]" data-testid="playbook-growing">
    <div className="text-3xl" aria-hidden>🌱</div>
    <h3 className="font-bold text-lg mt-2">Keep going, your playbook is growing</h3>
    <p className="text-sm text-mm-muted mt-1">Use a coping skill or calm tool {pb.min_uses} times and tap “Did that help?” afterwards. Check in {pb.min_checkins}+ days to see your good-day ingredients.</p>
    {pb.growing.length > 0 && <ul className="mt-3 space-y-1 text-sm">{pb.growing.map((g) => <li key={g.title}>{g.title}: {g.uses} of {g.needed} uses</li>)}</ul>}
  </div>
);

const MiaToggle = ({ on, onChange }) => (
  <label className="mm-card p-4 flex items-center justify-between gap-4" data-testid="playbook-mia-toggle-row">
    <span><span className="font-semibold block">Let Mia use my playbook</span><span className="text-sm text-mm-muted">Mia sees only this summary, never your journal, and never suggests things you've hidden.</span></span>
    <Switch checked={on} onCheckedChange={onChange} data-testid="playbook-mia-toggle" />
  </label>
);

const AddOwn = ({ onAdd }) => {
  const [text, setText] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) onAdd(text.trim()).then((ok) => ok && setText("")); }} className="flex gap-2 mt-3">
      <input className="mm-input" maxLength={280} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. Calling my sister, a hot shower, music in the car" data-testid="playbook-own-input" />
      <button className="mm-btn !px-4" disabled={!text.trim()} data-testid="playbook-own-add"><Plus size={16} />Add</button>
    </form>
  );
};

const markNew = (data, fresh) => {
  const tag = (l) => l.map((i) => ({ ...i, new: fresh.has(i.key) }));
  return { ...data, helpers: tag(data.helpers), ingredients: tag(data.ingredients), warnings: tag(data.warnings) };
};

const usePlaybook = () => {
  const [pb, setPb] = useState(null);
  const fresh = useRef(null);
  const load = useCallback(() => api.get("/playbook").then((r) => {
    if (!fresh.current) fresh.current = new Set([...r.data.helpers, ...r.data.ingredients, ...r.data.warnings].filter((i) => i.new).map((i) => i.key));
    setPb(markNew(r.data, fresh.current));
  }).catch((e) => { toast.error(errMsg(e)); setPb(false); }), []);
  useEffect(() => {
    load().then(() => api.post("/playbook/seen").then(() => window.dispatchEvent(new Event(PLAYBOOK_EVENT))).catch(() => {}));
  }, [load]);
  return [pb, setPb, load];
};

export default function Playbook() {
  const [pb, setPb, load] = usePlaybook();
  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); await load(); return true; } catch (e) { toast.error(errMsg(e)); return false; } };
  const pref = (key, state) => act(() => api.put("/playbook/item", { key, state }), state === "hidden" ? "Hidden. Mia won't suggest it either" : state === "pinned" ? "Pinned ⭐" : null);
  const setMia = (v) => { setPb((p) => ({ ...p, mia_playbook: v })); api.put("/playbook/settings", { mia_playbook: v }).catch((e) => toast.error(errMsg(e))); };

  if (pb === null) return <Spinner />;
  if (!pb) return null;
  const empty = !pb.helpers.length && !pb.ingredients.length && !pb.warnings.length;
  return (
    <div className="max-w-3xl pb-24" data-testid="playbook-page">
      <PageHeader back title="What Helps Me" subtitle="Your personal playbook, learned from your own check-ins and tools. Only you (and Mia, if you allow it) can see it." />
      <MiaToggle on={pb.mia_playbook} onChange={setMia} />
      {empty && <div className="mt-6"><Growing pb={pb} /></div>}
      {pb.helpers.length > 0 && (
        <Section title="Your top helpers" sub="Ranked by how much they eased stress or lifted you afterwards." testId="playbook-helpers">
          <div className="grid sm:grid-cols-2 gap-3">{pb.helpers.map((h) => <HelperCard key={h.key} h={h} onPref={pref} />)}</div>
        </Section>
      )}
      {!empty && pb.growing.length > 0 && <StillGrowing items={pb.growing} />}
      {pb.ingredients.length > 0 && (
        <Section title="Your good-day ingredients" sub="Things that tend to show up on your better days." testId="playbook-ingredients">
          <div className="grid sm:grid-cols-2 gap-3">{pb.ingredients.map((i) => <IngredientCard key={i.key} i={i} onPref={pref} />)}</div>
        </Section>
      )}
      {pb.warnings.length > 0 && (
        <Section title="Early signs to be gentle with yourself" sub="Patterns that sometimes come before a tougher day. Noticing them early is a strength." testId="playbook-warnings">
          <div className="grid gap-3">{pb.warnings.map((w) => <WarningCard key={w.key} w={w} onPref={pref} />)}</div>
        </Section>
      )}
      <Section title="Your own helpers" sub="Anything that works for you, in your own words." testId="playbook-own">
        {pb.own.length > 0 && <ul className="space-y-2">{pb.own.map((o) => <SavedLine key={o.id} q={o} testPrefix="playbook-own" onDelete={(id) => act(() => api.delete(`/playbook/own/${id}`))} />)}</ul>}
        <AddOwn onAdd={(text) => act(() => api.post("/playbook/own", { text }), "Added to your playbook 🌱")} />
      </Section>
      <Section title="Things you said helped" sub="Tap “This helped” on a journal entry, a Mia reply, a reframe or a letter to keep it here." testId="playbook-quotes">
        {pb.quotes.length > 0 ? <ul className="space-y-2">{pb.quotes.map((q) => <SavedLine key={q.id} q={q} testPrefix="playbook-quote" onDelete={(id) => act(() => api.delete(`/playbook/quotes/${id}`))} />)}</ul>
          : <p className="text-sm text-mm-muted" data-testid="playbook-quotes-empty">Nothing saved yet.</p>}
      </Section>
      {pb.hidden.length > 0 && (
        <Section title="Hidden (not for me)" testId="playbook-hidden">
          <div className="flex flex-wrap gap-2">{pb.hidden.map((h) => <button key={h.key} onClick={() => pref(h.key, "none")} className="mm-chip !text-xs" data-testid={`playbook-unhide-${h.key}`}>{h.title} · Show again</button>)}</div>
        </Section>
      )}
      <Section title="How to help me card" sub="A short card for the people who support you, built from your playbook and your own words." testId="playbook-help-card">
        <HelpCardEditor />
      </Section>
      <div className="flex flex-wrap gap-2 mt-10">
        <PlaybookPdfButton />
        <button onClick={() => downloadFile("/playbook/export", "mindful-me-playbook.json").catch((e) => toast.error(errMsg(e)))} className="mm-btn-ghost" data-testid="playbook-export"><Download size={16} />Download my playbook data</button>
      </div>
    </div>
  );
}
